import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
} from "@angular/common/http";
import { Inject, Injectable, Optional } from "@angular/core";
import { result } from "@node_modules/@types/lodash";
import { NotifyService } from "@node_modules/abp-ng2-module";
import {
  ExportInvoices,
  ExportReservations,
  ExportViolations,
} from "@shared/models";
import ExportExternalInvoices from "@shared/models/export-external-invoices";
import ExportParkingInvoices from "@shared/models/export-parking-invoices";
import { ExportSubscribtions } from "@shared/models/export-subscribtions";
import { API_BASE_URL } from "@shared/service-proxies/service-proxies";
import { error } from "console";
import * as moment from "moment";
import { Observable, Subject, catchError, defer, finalize, tap } from "rxjs";

@Injectable()
export class ExportExcelService {
  private http: HttpClient;
  private baseUrl: string;
  private loadingSubject = new Subject<boolean>();
  loading$: Observable<boolean> = this.loadingSubject.asObservable();

  constructor(
    @Inject(HttpClient) http: HttpClient,
    private notify: NotifyService,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  exportReservations(input: ExportReservations) {
    const query = input.toQueryString();
    const url = `${this.baseUrl}/api/Export/ExportReservationsDetailsToExcel?${query}`;

    this.loadingSubject.next(true);
    // Make the HTTP GET request
    this.http
      .get<any>(url, {
        observe: "response",
        responseType: "json", // Important to set responseType to 'blob'
      })
      .pipe(
        tap((res) => this.notify.success("Export has been registered, please check it in the Exports tab.", "Reservations")), //this.downloadFile(res, "Reservations")),
        catchError((err) => {
          console.error(err);
          this.notify.error("Please specify Start Date and End Date", "Reservations");
          return [];
        }),
        // Emit loading state false when the request is complete (either success or error)
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe();
  }

  exportSubcribtions(input: ExportSubscribtions) {
    const query = input.toQueryString();
    const url = `${this.baseUrl}/api/Export/ExportSubscriptions?${query}`;

    this.loadingSubject.next(true);
    // Make the HTTP GET request
    this.http
      .get<any>(url, {
        observe: "response",
        responseType: "json", // Important to set responseType to 'blob'
      })
      .pipe(
        tap((res) => this.notify.success("Export has been registered, please check it in the Exports tab.", "Subscriptions")),
        catchError((err) => {
          console.error(err);
          return [];
        }),
        // Emit loading state false when the request is complete (either success or error)
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe();
  }

  exportExternalInvoices(input: ExportExternalInvoices): Observable<any> {
    const query = input.toQueryString();
    const url = `${this.baseUrl}/api/Export/ExportExternalInvoicesToExcel?${query}`;

    this.loadingSubject.next(true);
    // Make the HTTP GET request
    return this.http
      .get<any>(url, {
        observe: "response",
        responseType: "json", // Important to set responseType to 'blob'
      })
      .pipe(
        tap((res) => this.notify.success("Export has been registered, please check it in the Exports tab.", "External Invoices")),
        catchError((err) => {
          console.error(err);
          return [];
        }),
        // Emit loading state false when the request is complete (either success or error)
        finalize(() => {
          this.loadingSubject.next(false);
          return false;
        })
      );
  }

  exportParkingInvoices(input: ExportParkingInvoices): Observable<any> {
    const query = input.toQueryString();
    const url = `${this.baseUrl}/api/Export/ExportParkingInvoicesToExcel?${query}`;

    this.loadingSubject.next(true);
    // Make the HTTP GET request
    return this.http
      .get<any>(url, {
        observe: "response",
        responseType: "json", // Important to set responseType to 'blob'
      })
      .pipe(
        tap((res) => this.notify.success("Export has been registered, please check it in the Exports tab.", "Parking Invoices")),
        catchError((err) => {
          console.error(err);
          return [];
        }),
        // Emit loading state false when the request is complete (either success or error)
        finalize(() => {
          this.loadingSubject.next(false);
          return false;
        })
      );
  }

  exportViolations(input: ExportViolations) {
    const query = input.toQueryString();
    const url = `${this.baseUrl}/api/Export/ExportViolationsToExcel?${query}`;
    this.loadingSubject.next(true);
    // Make the HTTP GET request
    this.http
      .get<any>(url, {
        observe: "response",
        responseType: "json", // Important to set responseType to 'blob'
      })
      .pipe(
        tap((res) => this.notify.success("Export has been registered, please check it in the Exports tab.", "Violations")),
        catchError((err) => {
          console.error(err);
          this.notify.error("Please specify Status, Start Date and End Date", "Violations");
          return [];
        }),
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe();
  }

  exportInvoices(input: ExportInvoices) {
    const query = input.toQueryString();
    const url = `${this.baseUrl}/api/Export/ExportInovicesToExcel?${query}`;
    this.loadingSubject.next(true);
    // Make the HTTP GET request
    this.http
      .get<any>(url, {
        observe: "response",
        responseType: "json", // Important to set responseType to 'blob'
      })
      .pipe(
        tap((res) => this.notify.success("Export has been registered, please check it in the Exports tab.", "Invoices")),
        catchError((err) => {
          console.error(err);
          this.notify.error("Please specify Start Date and End Date", "Invoices");
          return [];
        }),
        // Emit loading state false when the request is complete (either success or error)
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe();
  }

  public getUserExports(keyword: string, startDate?: string, endDate?: string, exportStatus?: string, reportType?: string, pageSize: number = 10, pageNumber: number = 1): Observable<any> {
    let url = `${this.baseUrl}/api/Export/GetUserExportRequests/requests?keyword=${keyword}&startDate=${startDate}&endDate=${endDate}&reportType=${reportType}&exportStatus=${exportStatus}&pageSize=${pageSize}&pageNumber=${pageNumber}`;
    this.loadingSubject.next(true);
    return defer(() => {
      return this.http.get(url).pipe(
        finalize(() => {
          this.loadingSubject.next(false);
        })
      );
    });
  }

  public downloadReport(id: string): Observable<any> {
    let url = `${this.baseUrl}/api/Export/DownloadReport/${id}`;
    this.loadingSubject.next(true);
    return this.http.get<any>(url).pipe(
      finalize(() => this.loadingSubject.next(false))
    );
  }



  public downloadFile(response: HttpResponse<Blob>, fileName: string): void {
    const contentDisposition = response.headers.get("content-disposition");
    fileName = `${fileName}-${moment().format("yyyyMMddHHmmss")}.xlsx`;

    const blob = new Blob([response.body], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }
}
